import type { Position } from "@engine/types/grid.js";
import { EventBus } from "@engine/EventBus.js";

// ── Per-map state ─────────────────────────────────────────────────────────────

interface MapState {
  exploredCells: Set<string>;
  openDoors: Set<string>;
  firedTriggers: Set<string>;
  /** NPC objectIds whose proximity bark has already fired this map session (N02). */
  firedBarks: Set<string>;
  /** Enemy `instanceId`s that died on this map and must not respawn (W06). */
  deadEnemies: Set<string>;
  /** Secret-door doorIds that have been revealed on this map (W12). */
  revealedSecretDoors: Set<string>;
  /** Trap IDs that have been revealed on this map (W18). */
  revealedTraps: Set<string>;
  /** Trap IDs that have been disarmed on this map (W18). */
  disarmedTraps: Set<string>;
  /** Container IDs that have been opened on this map (P16). */
  openedContainers: Set<string>;
  /** Container IDs whose contents have been taken (P16). */
  lootedContainers: Set<string>;
  /** Container IDs unlocked this map via key/lockpick/force (I16). */
  unlockedContainers: Set<string>;
  /** Door IDs unlocked this map via key/lockpick/force (I16). */
  unlockedDoors: Set<string>;
  /**
   * I17 — force-open attempts. Keyed by `"<kind>:<targetId>"`
   * (kind = "container" | "door"); value is the set of party-member
   * instanceIds who have tried. Each PC gets exactly one attempt per
   * target; the lock menu reads this to disable the button once every
   * member has burnt their shot.
   */
  forceOpenAttempts: Map<string, Set<string>>;
  /**
   * Floor-drop piles indexed by dropId (P16). Values carry the cell and
   * the list of itemIds still sitting in the pile — pickups remove the
   * entry rather than clearing the items array.
   */
  floorItemDrops: Map<string, { cell: Position; items: string[] }>;
  /**
   * Light IDs whose current state is "off" on this map (W22). Membership
   * is the single source of truth; `light_id ∈ lightsOff` ⇒ dark, absent
   * ⇒ lit. Seeded on map load via `setLightInitialState` so author
   * intent (`initial_state: "off"`) lands before any player toggle.
   */
  lightsOff: Set<string>;
  bossDead: boolean;
}

function freshMapState(): MapState {
  return {
    exploredCells: new Set(),
    openDoors: new Set(),
    firedTriggers: new Set(),
    firedBarks: new Set(),
    deadEnemies: new Set(),
    revealedSecretDoors: new Set(),
    revealedTraps: new Set(),
    disarmedTraps: new Set(),
    openedContainers: new Set(),
    lootedContainers: new Set(),
    unlockedContainers: new Set(),
    unlockedDoors: new Set(),
    forceOpenAttempts: new Map(),
    floorItemDrops: new Map(),
    lightsOff: new Set(),
    bossDead: false,
  };
}

function posKey(p: Position): string {
  return `${p.x},${p.z}`;
}

// ── Serialization types ───────────────────────────────────────────────────────

export interface MapStateJSON {
  exploredCells: string[];
  openDoors: string[];
  firedTriggers: string[];
  /** NPC objectIds whose proximity bark has fired (N02). Optional for load-time back-compat. */
  firedBarks?: string[];
  /** Enemy instanceIds that died on this map (W06). Optional for load-time back-compat. */
  deadEnemies?: string[];
  /** Secret-door doorIds revealed on this map (W12). Optional for load-time back-compat. */
  revealedSecretDoors?: string[];
  /** Trap IDs revealed on this map (W18). Optional for load-time back-compat. */
  revealedTraps?: string[];
  /** Trap IDs disarmed on this map (W18). Optional for load-time back-compat. */
  disarmedTraps?: string[];
  /** Container IDs opened on this map (P16). Optional for load-time back-compat. */
  openedContainers?: string[];
  /** Container IDs looted on this map (P16). Optional for load-time back-compat. */
  lootedContainers?: string[];
  /** Container IDs unlocked on this map (I16). Optional for load-time back-compat. */
  unlockedContainers?: string[];
  /** Door IDs unlocked on this map (I16). Optional for load-time back-compat. */
  unlockedDoors?: string[];
  /** Force-open attempts recorded per target on this map (I17). Optional for load-time back-compat. */
  forceOpenAttempts?: ForceOpenAttemptJSON[];
  /** Floor-drop piles on this map (P16). Optional for load-time back-compat. */
  floorItemDrops?: FloorDropJSON[];
  /** Light IDs whose current state is "off" on this map (W22). Optional for load-time back-compat. */
  lightsOff?: string[];
  bossDead: boolean;
}

export interface ForceOpenAttemptJSON {
  /** `"<kind>:<targetId>"` key. */
  targetKey: string;
  /** Party-member instanceIds who have tried. */
  memberIds: string[];
}

export interface FloorDropJSON {
  dropId: string;
  cell: Position;
  items: string[];
}

export interface WorldStoreJSON {
  currentMapId: string | null;
  maps: Record<string, MapStateJSON>;
}

// ── Store internals ───────────────────────────────────────────────────────────

const stateByMap = new Map<string, MapState>();
let currentMapId: string | null = null;

/**
 * Returns the active map's state, lazily creating it on first access.
 * Returns null when no map is active (operations silently no-op).
 */
function currentState(): MapState | null {
  if (currentMapId === null) return null;
  let s = stateByMap.get(currentMapId);
  if (!s) {
    s = freshMapState();
    stateByMap.set(currentMapId, s);
  }
  return s;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Per-map world state store.
 *
 * The active map is set via `setCurrentMap(mapId)`. All read/write operations
 * apply to that map's state only. Switching maps preserves the previous map's
 * state in the dictionary — returning to a map restores it exactly.
 *
 * State for maps never visited is created fresh on first access.
 */
export const WorldStore = {
  // ── Map lifecycle ──────────────────────────────────────────────────────────

  /**
   * Switch the active map. Preserves the outgoing map's state automatically
   * (it stays in the dictionary). The incoming map gets its existing state
   * restored, or a fresh state if this is the first visit.
   */
  setCurrentMap(mapId: string): void {
    currentMapId = mapId;
    if (!stateByMap.has(mapId)) {
      stateByMap.set(mapId, freshMapState());
    }
  },

  getCurrentMap(): string | null {
    return currentMapId;
  },

  /** True if this map has been visited (has a state entry). */
  hasVisited(mapId: string): boolean {
    return stateByMap.has(mapId);
  },

  // ── Explored cells ─────────────────────────────────────────────────────────

  markExplored(pos: Position): void {
    currentState()?.exploredCells.add(posKey(pos));
  },

  isExplored(pos: Position): boolean {
    return currentState()?.exploredCells.has(posKey(pos)) ?? false;
  },

  // ── Doors ──────────────────────────────────────────────────────────────────

  setDoorOpen(doorId: string, open: boolean): void {
    const s = currentState();
    if (!s) return;
    const was = s.openDoors.has(doorId);
    if (open) s.openDoors.add(doorId);
    else s.openDoors.delete(doorId);
    if (was !== open) {
      EventBus.emit("world:door_state_changed", { doorId, open });
    }
  },

  isDoorOpen(doorId: string): boolean {
    return currentState()?.openDoors.has(doorId) ?? false;
  },

  // ── Triggers ───────────────────────────────────────────────────────────────

  markTriggerFired(triggerId: string): void {
    currentState()?.firedTriggers.add(triggerId);
  },

  isTriggerFired(triggerId: string): boolean {
    return currentState()?.firedTriggers.has(triggerId) ?? false;
  },

  // ── Proximity barks (N02) ──────────────────────────────────────────────────

  markBarkFired(npcId: string): void {
    currentState()?.firedBarks.add(npcId);
  },

  isBarkFired(npcId: string): boolean {
    return currentState()?.firedBarks.has(npcId) ?? false;
  },

  // ── Dead enemies (W06) ─────────────────────────────────────────────────────

  /** Record an enemy as permanently dead on the active map (won't respawn). */
  markEnemyDead(instanceId: string): void {
    currentState()?.deadEnemies.add(instanceId);
  },

  /** True when this enemy was killed on the active map and should not respawn. */
  isEnemyDead(instanceId: string): boolean {
    return currentState()?.deadEnemies.has(instanceId) ?? false;
  },

  /** Snapshot of dead-enemy IDs on the active map (used by spawn flows). */
  getDeadEnemies(): readonly string[] {
    const s = currentState();
    return s ? [...s.deadEnemies] : [];
  },

  // ── Revealed secret doors (W12) ────────────────────────────────────────────

  /** Mark a secret door as revealed on the active map (idempotent). */
  markSecretDoorRevealed(doorId: string): void {
    currentState()?.revealedSecretDoors.add(doorId);
  },

  /** True when this secret door has already been discovered on the active map. */
  isSecretDoorRevealed(doorId: string): boolean {
    return currentState()?.revealedSecretDoors.has(doorId) ?? false;
  },

  // ── Revealed/disarmed traps (W18) ──────────────────────────────────────────

  /**
   * Mark a trap as revealed on the active map (idempotent). Emits
   * `world:trap_revealed` only on the first reveal so renderers don't
   * thrash on repeat checks.
   */
  markTrapRevealed(trapId: string, cell: Position): void {
    const s = currentState();
    if (!s) return;
    if (s.revealedTraps.has(trapId)) return;
    s.revealedTraps.add(trapId);
    EventBus.emit("world:trap_revealed", { trapId, cell });
  },

  isTrapRevealed(trapId: string): boolean {
    return currentState()?.revealedTraps.has(trapId) ?? false;
  },

  markTrapDisarmed(trapId: string): void {
    const s = currentState();
    if (!s) return;
    if (s.disarmedTraps.has(trapId)) return;
    s.disarmedTraps.add(trapId);
    EventBus.emit("world:trap_disarmed", { trapId });
  },

  isTrapDisarmed(trapId: string): boolean {
    return currentState()?.disarmedTraps.has(trapId) ?? false;
  },

  // ── Containers & floor drops (P16) ─────────────────────────────────────────

  /**
   * Mark a container as opened on the active map (idempotent). Emits
   * `world:container_opened` only on the first open.
   */
  markContainerOpened(containerId: string, cell: Position): void {
    const s = currentState();
    if (!s) return;
    if (s.openedContainers.has(containerId)) return;
    s.openedContainers.add(containerId);
    EventBus.emit("world:container_opened", { containerId, cell });
  },

  isContainerOpened(containerId: string): boolean {
    return currentState()?.openedContainers.has(containerId) ?? false;
  },

  /**
   * Mark a container's contents as taken (idempotent). Emits
   * `world:container_looted` only on the first loot.
   */
  markContainerLooted(containerId: string): void {
    const s = currentState();
    if (!s) return;
    if (s.lootedContainers.has(containerId)) return;
    s.lootedContainers.add(containerId);
    EventBus.emit("world:container_looted", { containerId });
  },

  isContainerLooted(containerId: string): boolean {
    return currentState()?.lootedContainers.has(containerId) ?? false;
  },

  // ── Container / door unlocking (I16) ───────────────────────────────────────

  /**
   * Mark a container as unlocked on the active map (idempotent). Emits
   * `world:container_unlocked` only on the first unlock so the UI can
   * dismiss the lock menu.
   */
  markContainerUnlocked(containerId: string): void {
    const s = currentState();
    if (!s) return;
    if (s.unlockedContainers.has(containerId)) return;
    s.unlockedContainers.add(containerId);
    EventBus.emit("world:container_unlocked", { containerId });
  },

  isContainerUnlocked(containerId: string): boolean {
    return currentState()?.unlockedContainers.has(containerId) ?? false;
  },

  /**
   * Mark a door as unlocked on the active map (idempotent). Emits
   * `world:door_unlocked` on the first unlock.
   */
  markDoorUnlocked(doorId: string): void {
    const s = currentState();
    if (!s) return;
    if (s.unlockedDoors.has(doorId)) return;
    s.unlockedDoors.add(doorId);
    EventBus.emit("world:door_unlocked", { doorId });
  },

  isDoorUnlocked(doorId: string): boolean {
    return currentState()?.unlockedDoors.has(doorId) ?? false;
  },

  // ── Force-open attempts (I17) ──────────────────────────────────────────────

  /**
   * Record that a party member has tried to force a lock open. Idempotent —
   * recording the same pair twice is a no-op. No event emission; the lock
   * menu reads state directly to refresh its button enablement.
   */
  recordForceOpenAttempt(
    targetKind: "container" | "door",
    targetId: string,
    memberId: string,
  ): void {
    const s = currentState();
    if (!s) return;
    const key = `${targetKind}:${targetId}`;
    let set = s.forceOpenAttempts.get(key);
    if (!set) {
      set = new Set();
      s.forceOpenAttempts.set(key, set);
    }
    set.add(memberId);
  },

  hasForceOpenAttempted(
    targetKind: "container" | "door",
    targetId: string,
    memberId: string,
  ): boolean {
    const s = currentState();
    if (!s) return false;
    return s.forceOpenAttempts.get(`${targetKind}:${targetId}`)?.has(memberId) ?? false;
  },

  /** Snapshot of who has attempted to force open a target. */
  getForceOpenAttempts(
    targetKind: "container" | "door",
    targetId: string,
  ): readonly string[] {
    const s = currentState();
    if (!s) return [];
    const set = s.forceOpenAttempts.get(`${targetKind}:${targetId}`);
    return set ? [...set] : [];
  },

  /**
   * Add a floor-drop pile on the active map. Idempotent per dropId — a
   * second call with the same dropId overwrites the prior entry. Emits
   * `world:item_drop_added` on every call so renderers can refresh.
   */
  addFloorDrop(dropId: string, cell: Position, items: readonly string[]): void {
    const s = currentState();
    if (!s) return;
    s.floorItemDrops.set(dropId, { cell, items: [...items] });
    EventBus.emit("world:item_drop_added", { dropId, cell, items });
  },

  /**
   * Remove a floor-drop pile (after pickup). Emits
   * `world:item_drop_picked_up` and returns true when the pile existed;
   * returns false (no event) for unknown ids so callers can fall back to
   * emitting the pickup event themselves for store-less legacy piles.
   */
  removeFloorDrop(dropId: string): boolean {
    const s = currentState();
    if (!s) return false;
    if (!s.floorItemDrops.has(dropId)) return false;
    s.floorItemDrops.delete(dropId);
    EventBus.emit("world:item_drop_picked_up", { dropId });
    return true;
  },

  /** Snapshot of floor-drop piles on the active map. */
  getFloorDrops(): readonly { dropId: string; cell: Position; items: readonly string[] }[] {
    const s = currentState();
    if (!s) return [];
    return [...s.floorItemDrops.entries()].map(([dropId, v]) => ({
      dropId,
      cell: v.cell,
      items: v.items,
    }));
  },

  // ── Lights (W22) ───────────────────────────────────────────────────────────

  /**
   * Seed a light's baseline state from its authored `initial_state`. Called
   * once per light during map load; idempotent and silent (no event). If
   * the light was already toggled at save/load time, `loadJSON` will have
   * populated `lightsOff` earlier and this call is a no-op on the matching
   * cases — we only add to the set, never remove, so a re-load of the map
   * can't un-toggle the author's intent.
   */
  setLightInitialState(lightId: string, initiallyOff: boolean): void {
    const s = currentState();
    if (!s) return;
    if (initiallyOff) s.lightsOff.add(lightId);
  },

  /**
   * Flip a light's on/off state. Emits `world:light_toggled` with the
   * resulting `off` boolean so the renderer can sync `PointLight.visible`
   * and NarrativeManager can push the observed `light_<id>_off` VAR.
   */
  toggleLight(lightId: string): void {
    const s = currentState();
    if (!s) return;
    const nextOff = !s.lightsOff.has(lightId);
    if (nextOff) s.lightsOff.add(lightId);
    else s.lightsOff.delete(lightId);
    EventBus.emit("world:light_toggled", { lightId, off: nextOff });
  },

  isLightOff(lightId: string): boolean {
    return currentState()?.lightsOff.has(lightId) ?? false;
  },

  /**
   * Absolute setter used by Ink's `light_<id>_off` observed-variable path
   * (W22). Emits `world:light_toggled` only when the state actually
   * changes — a redundant write from Ink is a silent no-op.
   */
  setLightOff(lightId: string, off: boolean): void {
    const s = currentState();
    if (!s) return;
    const current = s.lightsOff.has(lightId);
    if (current === off) return;
    if (off) s.lightsOff.add(lightId);
    else s.lightsOff.delete(lightId);
    EventBus.emit("world:light_toggled", { lightId, off });
  },

  // ── Boss ───────────────────────────────────────────────────────────────────

  setBossDead(): void {
    const s = currentState();
    if (s) s.bossDead = true;
  },

  isBossDead(): boolean {
    return currentState()?.bossDead ?? false;
  },

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /** Clear all per-map state and reset the active map pointer. */
  reset(): void {
    stateByMap.clear();
    currentMapId = null;
  },

  // ── Serialization ──────────────────────────────────────────────────────────

  toJSON(): WorldStoreJSON {
    const maps: Record<string, MapStateJSON> = {};
    for (const [mapId, s] of stateByMap) {
      maps[mapId] = {
        exploredCells: [...s.exploredCells],
        openDoors: [...s.openDoors],
        firedTriggers: [...s.firedTriggers],
        firedBarks: [...s.firedBarks],
        deadEnemies: [...s.deadEnemies],
        revealedSecretDoors: [...s.revealedSecretDoors],
        revealedTraps: [...s.revealedTraps],
        disarmedTraps: [...s.disarmedTraps],
        openedContainers: [...s.openedContainers],
        lootedContainers: [...s.lootedContainers],
        unlockedContainers: [...s.unlockedContainers],
        unlockedDoors: [...s.unlockedDoors],
        forceOpenAttempts: [...s.forceOpenAttempts.entries()].map(([targetKey, set]) => ({
          targetKey,
          memberIds: [...set],
        })),
        floorItemDrops: [...s.floorItemDrops.entries()].map(([dropId, v]) => ({
          dropId,
          cell: v.cell,
          items: [...v.items],
        })),
        lightsOff: [...s.lightsOff],
        bossDead: s.bossDead,
      };
    }
    return { currentMapId, maps };
  },

  loadJSON(data: WorldStoreJSON): void {
    stateByMap.clear();
    for (const [mapId, ms] of Object.entries(data.maps)) {
      const s = freshMapState();
      for (const cell of ms.exploredCells) s.exploredCells.add(cell);
      for (const id of ms.openDoors) s.openDoors.add(id);
      for (const id of ms.firedTriggers) s.firedTriggers.add(id);
      for (const id of ms.firedBarks ?? []) s.firedBarks.add(id);
      for (const id of ms.deadEnemies ?? []) s.deadEnemies.add(id);
      for (const id of ms.revealedSecretDoors ?? []) s.revealedSecretDoors.add(id);
      for (const id of ms.revealedTraps ?? []) s.revealedTraps.add(id);
      for (const id of ms.disarmedTraps ?? []) s.disarmedTraps.add(id);
      for (const id of ms.openedContainers ?? []) s.openedContainers.add(id);
      for (const id of ms.lootedContainers ?? []) s.lootedContainers.add(id);
      for (const id of ms.unlockedContainers ?? []) s.unlockedContainers.add(id);
      for (const id of ms.unlockedDoors ?? []) s.unlockedDoors.add(id);
      for (const entry of ms.forceOpenAttempts ?? []) {
        s.forceOpenAttempts.set(entry.targetKey, new Set(entry.memberIds));
      }
      for (const drop of ms.floorItemDrops ?? []) {
        s.floorItemDrops.set(drop.dropId, { cell: drop.cell, items: [...drop.items] });
      }
      for (const id of ms.lightsOff ?? []) s.lightsOff.add(id);
      s.bossDead = ms.bossDead;
      stateByMap.set(mapId, s);
    }
    currentMapId = data.currentMapId;
  },
};
